import { createModule } from 'saga-slice';
import { initialState, ProjectsState } from './stateType';
import {
  addToOnePlanSagaFactory,
  approveCrpSagaFactory,
  archiveProjectSagaFactory,
  bulkCreateSagaFactory,
  createMSTeamsSagaFactory,
  createProjectSagaFactory,
  deleteProjectSagaFactory,
  fetchProjectActivityLogSagaFactory,
  fetchProjectSagaFactory,
  fetchProjectsByStatusSagaFactory,
  fetchProjectsForBulkSagaFactory,
  fetchProjectsSagaFactory,
  fetchSiteLocationForBu,
  fetchSiteLocationSagaFactory,
  updatePartOfApprovedCrpSagaFactory,
  updatePartOfCrpSagaFactory,
  updateProjectSagaFactory,
  updateProjectsBulkSagaFactory,
  updateProjectsStatusSagaFactory,
  updateProjectStatusSagaFactory,
} from './sagas';
import { mergeProjectsArray } from './utils';
import {
  Project,
  ProjectSiteLocation,
  ProjectSolutionType,
  ProjectStatus,
} from '../../models/project';
import { ApprovedCRPPayloadType } from './types';

const Slice = createModule({
  name: 'projects',
  initialState,
  reducers: {
    fetchProjects: (state: ProjectsState) => {
      state.loading = true;
    },
    fetchProjectsSuccess: (state: ProjectsState, payload: Project[]) => {
      state.projects = payload;
      state.loading = false;
    },
    fetchProjectsError: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    setSelectedProject: (state: ProjectsState, payload: string) => {
      state.selectedProject = payload;
    },
    fetchProjectsByStatus: () => {
      // DO NOTHING
    },
    fetchProjectsByStatusSuccess: (
      state: ProjectsState,
      payload: Project[],
    ) => {
      mergeProjectsArray(payload, state);
    },
    fetchProjectsByStatusReplaceSuccess: (
      state: ProjectsState,
      payload: Project[],
    ) => {
      state.projects = payload;
    },
    fetchProjectsByStatusError: (state: ProjectsState, payload: string) => {
      state.error = payload;
    },
    updateProjectSolutions: (
      state: ProjectsState,
      payload: ProjectSolutionType[],
    ) => {
      state.solutions = payload;
    },
    fetchProject: (state: ProjectsState) => {
      state.loading = true;
    },
    fetchProjectActivityLog: (state: ProjectsState) => {
      state.loading = true;
    },
    fetchProjectSuccess: (
      state: ProjectsState,
      payload: Project & { dontAddToProjects?: boolean },
    ) => {
      state.loading = false;
      state.projectToEdit = payload;
      state.siteLocation = {
        id: payload?.siteLocationId,
        countryCode: payload?.countryCode,
        countryName: payload?.countryName,
        city: payload?.city,
        state: payload?.state,
        locationName: payload?.locationName,
        address: payload?.address,
      };
      // const existingProject = state.projects.findIndex(
      //   (p) => p.id === payload.id,
      // );
      if (payload.dontAddToProjects) return;
      // if (existingProject > -1) {
      //   state.projects[existingProject] = {
      //     ...state.projects[existingProject],
      //     ...payload,
      //   };
      // } else {
      //   state.projects = [...state.projects, payload];
      // }
    },
    fetchProjectActivityLogSuccess: (
      state: ProjectsState,
      payload: {
        content: any[];
      },
    ) => {
      state.loading = false;
      state.projectToEdit.activityLog = payload;
    },
    fetchProjectFailed: (state: ProjectsState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    setProjectToEdit: (state: ProjectsState, payload: Project) => {
      state.projectToEdit = payload;
    },
    clearProject: (state: ProjectsState) => {
      state.projectToEdit = null;
    },
    createNewProject: (state: ProjectsState) => {
      state.loading = true;
    },
    createNewProjectSuccess: (state: ProjectsState, payload: Project) => {
      state.loading = false;
      const projectWithTimestamp = {
        ...payload,
        lastUpdatedDate: new Date().toISOString(),
      };
      state.projects = [projectWithTimestamp, ...state.projects];
    },
    updateProject: (state: ProjectsState) => {
      state.loading = true;
    },
    updateProjectSuccess: (state: ProjectsState, payload: Project) => {
      state.loading = false;
      const projectWithTimestamp = {
        ...payload,
        lastUpdatedDate: new Date().toISOString(),
      };
      state.projects = state.projects.map((p) =>
        p.id === projectWithTimestamp.id ? projectWithTimestamp : p,
      );
    },
    updateProjectError: (state: ProjectsState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    createNewProjectFailed: (state: ProjectsState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    updateProjectsStatus: (state: ProjectsState) => {
      state.loading = true;
    },
    addSolution: (state: ProjectsState, payload: ProjectSolutionType) => {
      state.solutions = [...state.solutions, payload];
    },
    addSolutions: (state: ProjectsState, payload: ProjectSolutionType[]) => {
      const originalSolutions = state.solutions.filter(
        (solution) => !solution.id.includes('unsaved-'),
      );
      state.solutions = [...originalSolutions, ...payload];
    },
    removeSolution: (state: ProjectsState, payload: string) => {
      state.solutions = state.solutions.filter((s) => s.id !== payload);
    },
    editSolution: (state: ProjectsState, payload: ProjectSolutionType) => {
      state.solutions = state.solutions.map((s) =>
        s.id === payload.id ? payload : s,
      );
    },
    clearSolutions: (state: ProjectsState) => {
      state.solutions = [];
    },
    updateProjectsStatusSuccess: (
      state: ProjectsState,
      payload: { projects: string[]; status: string },
    ) => {
      const projects = state.projects;
      payload.projects.forEach((projectId) => {
        const index = projects.findIndex((project) => project.id === projectId);
        if (index > -1)
          projects[index] = {
            ...projects[index],
            projectStatus: ProjectStatus[payload.status],
          };
      });
      state.loading = false;
    },
    deleteProjects: (state: ProjectsState) => {
      state.loading = true;
    },
    deleteProjectSuccess: (state: ProjectsState, payload: string) => {
      state.projects = state.projects.filter((p) => payload !== p.id);
      state.loading = false;
    },
    deleteProjectError: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    updateProjectsStatusFailed: (state: ProjectsState, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    updateProjectStatus: (state: ProjectsState) => {
      state.loading = true;
    },
    updateProjectStatusSuccess: (
      state: ProjectsState,
      payload: {
        projectId: string;
        status: ProjectStatus;
      },
    ) => {
      state.projects = state.projects.map((p) => {
        if (p.id === payload.projectId) {
          return { ...p, projectStatus: payload.status };
        } else {
          return p;
        }
      });
      if (state.projectToEdit) {
        state.projectToEdit.projectStatus = payload.status;
      }
      state.loading = false;
    },
    updateProjectStatusFailed: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    setSolutions: (state: ProjectsState, payload: ProjectSolutionType[]) => {
      state.solutions = payload;
    },
    fetchSiteLocation: () => {
      // do nothing
    },
    changeBuForBulkCreateProject: () => {
      // do nothing
    },
    setSiteLocation: (state: ProjectsState, payload: ProjectSiteLocation) => {
      state.siteLocation = payload;
    },
    updatePartOfCrp(state: ProjectsState) {
      state.loading = true;
    },
    updatePartOfCrpSuccess(state: ProjectsState, payload: any[]) {
      state.loading = false;
      state.projects = state.projects.map((project) => {
        const updateData = payload[project.id];
        if (updateData) {
          return {
            ...project,
            partOfCrp: updateData.partOfCrp,
          };
        }
        return project;
      });
    },
    updatePartOfCrpFailed(state: ProjectsState, payload: string) {
      state.error = payload;
      state.loading = false;
    },
    updatePartOfApprovedCrp: (state: ProjectsState) => {
      state.loading = true;
    },
    updatePartOfApprovedCrpSuccess: (
      state: ProjectsState,
      payload: ApprovedCRPPayloadType,
    ) => {
      state.loading = false;
      state.projects = state.projects.map((project) => {
        const updateData = payload[project.id];
        if (updateData) {
          return {
            ...project,
            partOfCrp: updateData.partOfCrp,
          };
        }
        return project;
      });
    },
    updatePartOfApprovedCrpFailed: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    approveCrp: (state: ProjectsState) => {
      state.loading = true;
    },
    approveCrpSuccess: (
      state: ProjectsState,
      payload: ApprovedCRPPayloadType,
    ) => {
      state.loading = false;
      if (payload.moveQualifiedToDraft) {
        state.projects = state.projects.map((p) =>
          payload.projects.includes(p.id)
            ? { ...p, projectStatus: ProjectStatus.DRAFT }
            : p,
        );
      }
    },
    approveCrpFailed: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    archiveProjects: (state: ProjectsState) => {
      state.loading = true;
    },
    archiveProjectSuccess: (state: ProjectsState, payload: string) => {
      state.projects = state.projects.filter((p) => p.id !== payload);
      state.loading = false;
    },
    archiveProjectError: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    addToOnePlan: (state: ProjectsState) => {
      state.loading = true;
    },
    createMSTeams: (state: ProjectsState) => {
      state.loading = true;
    },
    addToOnePlanSuccess: (state: ProjectsState) => {
      state.loading = false;
      state.projectToEdit.partOfOnePlan = true;
    },
    createMSTeamsSuccess: (state: ProjectsState, payload: string) => {
      state.loading = false;
      state.projectToEdit.msTeamsUrl = payload;
    },
    addToOnePlanFailed: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    createMSTeamsFailed: (state: ProjectsState, payload: string) => {
      state.error = payload;
      state.loading = false;
    },
    setProjectsForBulk: (state: ProjectsState, payload: string[]) => {
      state.projectsForBulk = payload;
    },
    fetchProjectsForBulk: (state) => {
      state.loading = true;
    },
    fetchProjectsForBulkError: (state, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    updateProjectsBulk: (state) => {
      state.loading = true;
    },
    updateProjectsBulkSuccess: (state, payload: Project[]) => {
      state.loading = false;
      const now = new Date().toISOString();
      payload.forEach((project) => {
        const index = state.projects.findIndex((p) => p.id === project.id);
        if (index > -1) {
          state.projects[index] = {
            ...state.projects[index],
            ...project,
            lastUpdatedDate: now,
          };
        }
      });
    },
    updateProjectsBulkError: (state, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
    createProjectsBulk: (state) => {
      state.loading = true;
    },
    createProjectsBulkSuccess: (state, payload: Project[]) => {
      state.loading = false;
      const now = new Date().toISOString();
      const projectsWithTimestamp = payload.map((p) => ({
        ...p,
        lastUpdatedDate: now,
      }));
      state.projects = [...projectsWithTimestamp, ...state.projects];
    },
    createProjectsBulkError: (state, payload: string) => {
      state.loading = false;
      state.error = payload;
    },
  },
  sagas: (actions) => ({
    [actions.fetchProjects]: fetchProjectsSagaFactory(actions),
    [actions.fetchProjectsByStatus]: fetchProjectsByStatusSagaFactory(
      actions,
      false,
    ),
    [actions.createProjectsBulk]: bulkCreateSagaFactory(actions),
    [actions.fetchProject]: fetchProjectSagaFactory(actions),
    [actions.fetchProjectActivityLog]:
      fetchProjectActivityLogSagaFactory(actions),
    [actions.createNewProject]: createProjectSagaFactory(actions),
    [actions.updateProjectsStatus]: updateProjectsStatusSagaFactory(actions),
    [actions.updateProjectStatus]: updateProjectStatusSagaFactory(actions),
    [actions.updateProject]: updateProjectSagaFactory(actions),
    [actions.fetchSiteLocation]: fetchSiteLocationSagaFactory(actions),
    [actions.changeBuForBulkCreateProject]: fetchSiteLocationForBu(),
    [actions.approveCrp]: approveCrpSagaFactory(actions),
    [actions.updatePartOfCrp]: updatePartOfCrpSagaFactory(actions),
    [actions.updatePartOfApprovedCrp]:
      updatePartOfApprovedCrpSagaFactory(actions),
    [actions.archiveProjects]: archiveProjectSagaFactory(actions),
    [actions.deleteProjects]: deleteProjectSagaFactory(actions),
    [actions.addToOnePlan]: addToOnePlanSagaFactory(actions),
    [actions.createMSTeams]: createMSTeamsSagaFactory(actions),
    [actions.fetchProjectsForBulk]: fetchProjectsForBulkSagaFactory(actions),
    [actions.updateProjectsBulk]: updateProjectsBulkSagaFactory(actions),
  }),
});

export default Slice;

export const projectsActions = Slice.actions;
